import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNavigateBack } from '../hooks/listUrlState'
import { SIMPLE_COPY } from '@proffer/shared'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { apiGet, apiPost } from '../config/api'
import {
  generateOfferText,
  OfferCreateHttpError,
  type GenerateOfferPayload,
} from '../lib/offer-create-api'
import {
  buildCustomersListPath,
  crmCustomersSwrKey,
  DEFAULT_CUSTOMER_LIST_QUERY,
  type CustomerDto,
} from '../types/customer'
import type { OfferDto } from '../types/offer'
import { OfferAiInstructionPanel } from './offers/OfferAiInstructionPanel'
import { VoiceNotesRecorder } from './VoiceNotesRecorder'
import { Alert } from './UI/Alert'
import { Button, ButtonLink } from './UI/Button'
import { CustomerSelect } from './customers/CustomerSelect'
import { useCustomerIdFromUrl } from '../hooks/useCustomerIdFromUrl'
import { formatDateEl } from './UI'
import { formLabelClass, Textarea, TextInput } from './UI/Form'

async function postGenerate(
  _key: string,
  { arg }: { arg: GenerateOfferPayload },
) {
  return generateOfferText(arg)
}

export function CreateOffer() {
  const navigate = useNavigate()
  const goBack = useNavigateBack('/offers')
  const [title, setTitle] = useState('')
  const [amountStr, setAmountStr] = useState('')
  const [rawNotes, setRawNotes] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [editedText, setEditedText] = useState('')
  const [aiInstruction, setAiInstruction] = useState('')
  const [showEditor, setShowEditor] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)

  const {
    data: customers,
    error: customersError,
    isLoading: customersLoading,
    mutate: mutateCustomers,
  } = useSWR<CustomerDto[]>(
    crmCustomersSwrKey(DEFAULT_CUSTOMER_LIST_QUERY),
    () => apiGet<CustomerDto[]>(buildCustomersListPath(DEFAULT_CUSTOMER_LIST_QUERY)),
  )

  const refreshCustomers = useCallback(
    () => mutateCustomers(),
    [mutateCustomers],
  )

  useCustomerIdFromUrl(setCustomerId, { mutateCustomers: refreshCustomers })

  const { trigger, isMutating, error, reset } = useSWRMutation(
    'offers-generate',
    postGenerate,
  )

  const buildGeneratePayload = (
    withCurrentText: boolean,
  ): GenerateOfferPayload => {
    const payload: GenerateOfferPayload = {
      raw_notes: rawNotes,
      customerId: customerId || undefined,
    }
    const instruction = aiInstruction.trim()
    if (instruction) payload.ai_instruction = instruction
    const amountTrim = amountStr.trim().replace(',', '.')
    if (amountTrim) {
      const n = Number(amountTrim)
      if (Number.isFinite(n) && n >= 0) payload.amount = n
    }
    if (withCurrentText) {
      const current = editedText.trim()
      if (current) payload.current_text = current
    }
    return payload
  }

  const canRegenerateAi =
    Boolean(rawNotes.trim()) &&
    Boolean(editedText.trim() || aiInstruction.trim())

  const runGenerate = async (withCurrentText: boolean) => {
    if (!rawNotes.trim() || isMutating) return
    if (withCurrentText && !canRegenerateAi) return
    reset()
    try {
      const result = await trigger(buildGeneratePayload(withCurrentText))
      setEditedText(result.generated_text)
      setShowEditor(true)
    } catch (e) {
      if (e instanceof OfferCreateHttpError && e.status === 403) {
        setUpgradeModalOpen(true)
        reset()
      }
    }
  }

  const handleGenerate = () => void runGenerate(false)
  const handleRegenerate = () => void runGenerate(true)

  const canSave =
    Boolean(customerId) &&
    Boolean(rawNotes.trim()) &&
    !saving &&
    !customersLoading

  const handleSave = async () => {
    if (!canSave) return
    setSaveError(null)
    const amountTrim = amountStr.trim().replace(',', '.')
    let amount: number | undefined
    if (amountTrim) {
      const n = Number(amountTrim)
      if (!Number.isFinite(n) || n < 0) {
        setSaveError('Μη έγκυρο ποσό.')
        return
      }
      amount = n
    }
    setSaving(true)
    try {
      const gen = editedText.trim()
      const payload: Record<string, unknown> = {
        customerId,
        raw_notes: rawNotes,
        title: title.trim() || undefined,
        generated_text: gen.length > 0 ? gen : undefined,
        amount,
      }
      const created = await apiPost<OfferDto>('/offers', payload)
      navigate(`/offers/${created.id}`)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Η αποθήκευση απέτυχε.')
    } finally {
      setSaving(false)
    }
  }

  const labelCls = formLabelClass
  const offerDateDisplay = formatDateEl(new Date().toISOString())

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6 text-left">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex text-sm font-medium text-violet-600 hover:text-violet-500 dark:text-violet-400"
        >
          ← Πίσω
        </button>
      </div>
      <div className="mb-8 text-left">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
          {SIMPLE_COPY.advancedCreateTitle}
        </p>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl">
          Πλήρης δημιουργία προσφοράς
        </h1>
        <p className="mt-3 max-w-xl text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
          Γράψε ή υπαγόρευσε τις σημειώσεις, πρόσθεσε τίτλο και ποσό, διόρθωσε το
          κείμενο και αποθήκευσε.
        </p>
      </div>

      <div className="rounded-3xl border border-zinc-200/90 bg-white/90 p-6 shadow-xl shadow-zinc-200/40 backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/75 dark:shadow-black/30 sm:p-8">
        <div className="flex flex-col gap-8">
          <CustomerSelect
            id="customer"
            value={customerId}
            onChange={setCustomerId}
            customers={customers}
            loading={customersLoading}
            error={customersError}
            clearable
            onClear={() => setCustomerId('')}
          />

          <div className="text-left">
            <label htmlFor="offer_title" className={labelCls}>
              Τίτλος <span className="font-normal text-zinc-500">(προαιρετικό)</span>
            </label>
            <TextInput
              id="offer_title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="π.χ. Ανακαίνιση μπάνιου — Χαλάνδρι"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-left">
            <div className="min-w-0">
              <label htmlFor="offer_amount" className={labelCls}>
                Ποσό (€){' '}
                <span className="font-normal text-zinc-500">(προαιρετικό)</span>
              </label>
              <TextInput
                id="offer_amount"
                type="text"
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="π.χ. 1250.00"
                controlSize="lg"
                className="w-full"
              />
            </div>
            <div className="min-w-0">
              <p className={labelCls}>Ημερομηνία προσφοράς</p>
              <p
                className="flex h-[54px] w-full items-center rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-base text-zinc-800 dark:border-zinc-700 dark:bg-zinc-950/50 dark:text-zinc-200"
                aria-readonly="true"
              >
                {offerDateDisplay}
              </p>
              <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                Ορίζεται αυτόματα κατά την αποθήκευση.
              </p>
            </div>
          </div>

          <div className="text-left">
            <div className="mb-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <label htmlFor="raw_notes" className={`${labelCls} mb-0 sm:pt-1`}>
                Πρόχειρες σημειώσεις
              </label>
              <VoiceNotesRecorder
                disabled={isMutating || saving}
                onTranscribed={(text) =>
                  setRawNotes((prev) => {
                    const p = prev.trim()
                    return p ? `${p}\n${text}` : text
                  })
                }
              />
            </div>
            <Textarea
              id="raw_notes"
              value={rawNotes}
              onChange={(e) => setRawNotes(e.target.value)}
              placeholder="Πληκτρολόγησε ή χρησιμοποίησε το μικρόφωνο παραπάνω…"
              rows={8}
              className="min-h-[220px] resize-y py-4 text-lg leading-relaxed sm:text-xl"
            />
          </div>

          <OfferAiInstructionPanel
            aiInstruction={aiInstruction}
            onAiInstructionChange={setAiInstruction}
            onGenerate={handleGenerate}
            onRegenerate={handleRegenerate}
            isBusy={isMutating || saving}
            canGenerate={Boolean(rawNotes.trim())}
            canRegenerate={canRegenerateAi}
            error={error?.message ?? null}
            instructionId="create-offer-ai-instruction"
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={() => void handleSave()}
              disabled={!canSave}
            >
              {saving ? 'Αποθήκευση…' : 'Αποθήκευση'}
            </Button>
          </div>

          {saveError && (
            <Alert tone="error" className="text-left">
              {saveError}
            </Alert>
          )}

          {isMutating && (
            <div
              className="flex items-center justify-center gap-3 rounded-2xl border border-violet-200/80 bg-violet-50/90 px-4 py-4 text-center dark:border-violet-500/20 dark:bg-violet-950/50"
              role="status"
              aria-live="polite"
            >
              <span
                className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-violet-600 border-t-transparent dark:border-violet-400"
                aria-hidden
              />
              <span className="font-medium text-violet-900 dark:text-violet-200">
                Το AI ετοιμάζει την προσφορά σου…
              </span>
            </div>
          )}

          {upgradeModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              role="dialog"
              aria-modal
              aria-labelledby="upgrade-modal-title"
            >
              <div className="max-w-md rounded-3xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900">
                <h2
                  id="upgrade-modal-title"
                  className="text-lg font-semibold text-zinc-900 dark:text-white"
                >
                  Αναβάθμιση σε Basic
                </h2>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                  Έφτασες το όριο δωρεάν προσφορών AI. Με το Basic αποκτάς:
                </p>
                <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-zinc-700 dark:text-zinc-300">
                  <li>Απεριόριστες παραγωγές προσφορών</li>
                  <li>Προσαρμοσμένο λογότυπο</li>
                  <li>Εξαγωγή PDF</li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-2">
                  <ButtonLink
                    to="/billing"
                    variant="solid"
                    className="inline-flex flex-1 items-center justify-center rounded-2xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow transition hover:bg-violet-500"
                    onClick={() => setUpgradeModalOpen(false)}
                  >
                    Μετάβαση στην πληρωμή
                  </ButtonLink>
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex-1"
                    onClick={() => setUpgradeModalOpen(false)}
                  >
                    Κλείσιμο
                  </Button>
                </div>
              </div>
            </div>
          )}

          {(showEditor || editedText) && (
            <div className="border-t border-zinc-200 pt-8 text-left dark:border-zinc-700">
              <label htmlFor="generated" className={labelCls}>
                Κείμενο προσφοράς (AI)
              </label>
              <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-500">
                Επεξεργάσου το κείμενο πριν το αντιγράψεις ή το αποθηκεύσεις.
              </p>
              <Textarea
                id="generated"
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                rows={14}
                className="min-h-[280px] resize-y bg-zinc-50/90 leading-relaxed dark:bg-zinc-950/50 sm:text-lg"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
